/**
 * `useDashboardNavigate()` — the only place dashboard URLs get
 * assembled. Components call typed methods (`goToTab`, `openEntity`,
 * `setPeek`, ...); URL strings live here. See
 * `docs/architecture/dashboard-routing.md`.
 *
 * The dashboard router uses basepath '/dashboard', so the `to:`
 * passed to TanStack `navigate` is the path *after* '/dashboard'.
 */

import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  useDashboardRoute,
  type DashboardRouteQuery,
  type DetailTabToken,
  type EntityKindToken,
  type TabToken,
} from './useDashboardRoute'

export function buildFolderPath(
  folderId: string,
  tab?: TabToken | null,
  entityKind?: EntityKindToken | null,
  entityId?: string | null,
  detailTab?: DetailTabToken | null,
): string {
  let path = `/${encodeURIComponent(folderId)}`
  if (tab) path += `/${encodeURIComponent(tab)}`
  if (tab && entityKind && entityId) {
    path += `/${encodeURIComponent(entityKind)}/${encodeURIComponent(entityId)}`
    if (detailTab) path += `/${encodeURIComponent(detailTab)}`
  }
  return path
}

export interface DashboardNavigate {
  /** Switch the workspace tab on the current folder. Drops any open
   *  entity takeover and detail sub-tab. */
  goToTab(tab: TabToken): void

  /** Open an entity in the takeover slot. Optional sub-tab. */
  openEntity(
    kind: EntityKindToken,
    id: string,
    detailTab?: DetailTabToken,
  ): void

  /** Close the entity takeover, return to the current tab. */
  closeEntity(): void

  /** Change the entity detail sub-tab while takeover is open. */
  setDetailTab(detailTab: DetailTabToken): void

  /** Open / close the peek panel (transient query state). */
  setPeek(entityId: string | null): void

  /** Set / clear the scope filter for the current tab. */
  setScope(scopeId: string | null): void

  /** Set / clear the search query for the current tab. */
  setSearchQuery(q: string | null): void

  /** Set / clear the view mode (gallery cards-vs-table, lens, ...). */
  setViewMode(view: string | null): void
}

export function useDashboardNavigate(): DashboardNavigate {
  const navigate = useNavigate()
  const route = useDashboardRoute()

  const push = useCallback(
    (
      next: {
        folderId?: string | null
        tab?: TabToken | null
        entityKind?: EntityKindToken | null
        entityId?: string | null
        detailTab?: DetailTabToken | null
      },
      queryPatch?: Partial<DashboardRouteQuery>,
      replace = false,
    ) => {
      const folderId = next.folderId ?? route.folderId
      if (!folderId) return // can't navigate without a folder context
      const to = buildFolderPath(
        folderId,
        next.tab,
        next.entityKind,
        next.entityId,
        next.detailTab,
      )
      const search: DashboardRouteQuery = { ...route.query, ...(queryPatch ?? {}) }
      // Prune null/undefined/empty.
      for (const key of Object.keys(search) as (keyof DashboardRouteQuery)[]) {
        if (!search[key]) delete search[key]
      }
      void navigate({
        to,
        search,
        replace,
      } as never)
    },
    [navigate, route],
  )

  return {
    goToTab: useCallback(
      (tab) => push({ tab, entityKind: null, entityId: null, detailTab: null }, { view: undefined }),
      [push],
    ),
    openEntity: useCallback(
      (kind, id, detailTab) =>
        push({ tab: route.tab, entityKind: kind, entityId: id, detailTab: detailTab ?? null }),
      [push, route.tab],
    ),
    closeEntity: useCallback(
      () =>
        push({
          tab: route.tab,
          entityKind: null,
          entityId: null,
          detailTab: null,
        }),
      [push, route.tab],
    ),
    setDetailTab: useCallback(
      (detailTab) =>
        push({
          tab: route.tab,
          entityKind: route.entityKind,
          entityId: route.entityId,
          detailTab,
        }),
      [push, route.tab, route.entityKind, route.entityId],
    ),
    setPeek: useCallback(
      (entityId) =>
        push(
          {
            tab: route.tab,
            entityKind: route.entityKind,
            entityId: route.entityId,
            detailTab: route.detailTab,
          },
          { peek: entityId ?? undefined },
          true, // replace — peek toggle shouldn't pollute history
        ),
      [push, route.tab, route.entityKind, route.entityId, route.detailTab],
    ),
    setScope: useCallback(
      (scopeId) =>
        push(
          {
            tab: route.tab,
            entityKind: route.entityKind,
            entityId: route.entityId,
            detailTab: route.detailTab,
          },
          { scope: scopeId ?? undefined },
          true,
        ),
      [push, route.tab, route.entityKind, route.entityId, route.detailTab],
    ),
    setSearchQuery: useCallback(
      (q) =>
        push(
          {
            tab: route.tab,
            entityKind: route.entityKind,
            entityId: route.entityId,
            detailTab: route.detailTab,
          },
          { q: q ?? undefined },
          true,
        ),
      [push, route.tab, route.entityKind, route.entityId, route.detailTab],
    ),
    setViewMode: useCallback(
      (view) =>
        push(
          {
            tab: route.tab,
            entityKind: route.entityKind,
            entityId: route.entityId,
            detailTab: route.detailTab,
          },
          { view: view ?? undefined },
          true,
        ),
      [push, route.tab, route.entityKind, route.entityId, route.detailTab],
    ),
  }
}
